// src/features/chales/ChalesTab.jsx
import React, { useState, useMemo } from "react";
import { useChalesData } from "./hooks/useChalesData";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import ChaletCard from "./components/ChaletCard";
import AddReservationModal from "./components/AddReservationModal";
import EditReservationModal from "./components/EditReservationModal";
import { formatDateToYYYYMMDD } from "utils/dateUtils";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";
import ChaletList from "./components/ChaletList";
import ExportGuestsModal from "./components/ExportGuestsModal";
import DuplicateChalesAlert from "./components/DuplicateChalesAlert";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    value || 0,
  );

function ChalesTab({ userRole, searchTerm, initialDate }) {
  const [selectedDate, setSelectedDate] = useState(initialDate ? new Date(initialDate) : new Date());
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [isGuestListModalOpen, setIsGuestListModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const {
    chales,
    isLoading,
    error,
    addPayment,
    removePayment,
    totals,
    launchFinancials,
    isLaunching,
    addReservation,
    deleteReservation,
    updateReservation,
    addCredit,
  } = useChalesData(selectedDate, searchTerm);

  const handleDateChange = (event) => {
    setSelectedDate(new Date(event.target.value + "T00:00:00"));
  };

  const handleOpenEditModal = (chaletData) => {
    setEditingReservation(chaletData);
  };

  const availableChales = useMemo(() => {
    return chales.filter((c) => c.isAvailable).map((c) => c.chaletNumber);
  }, [chales]);

  const canEdit = userRole === "admin" || userRole === "editor";
  const isSearchMode = searchTerm && searchTerm.length > 3;

  const hasCheckIns = useMemo(() => {
    return chales.some(
      (c) =>
        !c.isAvailable &&
        c.checkInDate &&
        c.checkInDate.getFullYear() === selectedDate.getFullYear() &&
        c.checkInDate.getMonth() === selectedDate.getMonth() &&
        c.checkInDate.getDate() === selectedDate.getDate(),
    );
  }, [chales, selectedDate]);

  return (
    <div className="space-y-6">
      <DuplicateChalesAlert 
        userRole={userRole} 
        onSelectDate={(date) => setSelectedDate(new Date(date))} 
      />

      <div className="flex flex-wrap justify-between items-end gap-4">
        <div className="w-auto">
          {isSearchMode ? (
            <div className="p-2 bg-blue-100 text-blue-800 rounded border border-blue-200">
              <strong>Modo de Busca:</strong> Exibindo resultados para '
              {searchTerm}'
            </div>
          ) : (
            <>
              <Label htmlFor="chale-date" className="font-semibold">
                Selecione a Data
              </Label>
              <Input
                type="date"
                id="chale-date"
                value={formatDateToYYYYMMDD(selectedDate)}
                onChange={handleDateChange}
                className="mt-2 force-light-datepicker"
              />
            </>
          )}
        </div>

        {canEdit && !isSearchMode && (
          <div className="flex gap-2">
            <Button onClick={() => setIsAddModalOpen(true)}>
              + Nova Reserva
            </Button>
            {hasCheckIns && (
              <Button
                variant="outline"
                onClick={() => setIsGuestListModalOpen(true)}
              >
                + Lista de Hóspedes
              </Button>
            )}
          </div>
        )}
      </div>

      {isLoading && <LoadingSpinner />}
      {error && (
        <p className="text-red-500 text-center p-4 bg-red-50 rounded-md">
          Erro: {error}
        </p>
      )}

      {!isLoading && !error && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {chales.map((chale) => (
              <ChaletCard
                key={chale.chaletNumber}
                chale={chale}
                allChales={chales}
                onAddPayment={addPayment}
                onRemovePayment={removePayment}
                onDeleteReservation={deleteReservation}
                onEditReservation={handleOpenEditModal}
                onAddCredit={addCredit}
                canEdit={canEdit}
              />
            ))}
          </div>

          {!isSearchMode && (
            <div className="mt-4 flex justify-end">
              {hasCheckIns && (
                <Button
                  variant="secondary"
                  onClick={() => setIsExportModalOpen(true)}
                >
                  Exportar Hóspedes
                </Button>
              )}
            </div>
          )}

          {!isSearchMode && (
            <div className="mt-6 md:mt-8 p-4 md:p-6 bg-white dark:bg-card/80 border border-gray-200 dark:border-green-900/60 rounded-lg shadow-sm">
              <h3 className="text-lg md:text-xl font-bold text-gray-800 dark:text-green-100 mb-4">
                Totalização do Dia
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4 text-center">
                <div className="p-2 md:p-3 bg-primary/5 dark:bg-green-900/40 rounded-lg border border-primary/10 dark:border-green-800/60 overflow-hidden shadow-sm">
                  <p className="text-xs md:text-sm text-primary/70 dark:text-green-300/80 font-medium">Depósito</p>
                  <p className="text-base md:text-lg font-bold text-primary dark:text-green-50 truncate" title={formatCurrency(totals.depósito)}>
                    {formatCurrency(totals.depósito)}
                  </p>
                </div>
                <div className="p-2 md:p-3 bg-primary/5 dark:bg-green-900/40 rounded-lg border border-primary/10 dark:border-green-800/60 overflow-hidden shadow-sm">
                  <p className="text-xs md:text-sm text-primary/70 dark:text-green-300/80 font-medium">PIX</p>
                  <p className="text-base md:text-lg font-bold text-primary dark:text-green-50 truncate" title={formatCurrency(totals.pix)}>
                    {formatCurrency(totals.pix)}
                  </p>
                </div>
                <div className="p-2 md:p-3 bg-primary/5 dark:bg-green-900/40 rounded-lg border border-primary/10 dark:border-green-800/60 overflow-hidden shadow-sm">
                  <p className="text-xs md:text-sm text-primary/70 dark:text-green-300/80 font-medium">Cartão</p>
                  <p className="text-base md:text-lg font-bold text-primary dark:text-green-50 truncate" title={formatCurrency(totals.cartão)}>
                    {formatCurrency(totals.cartão)}
                  </p>
                </div>
                <div className="p-2 md:p-3 bg-primary/5 dark:bg-green-900/40 rounded-lg border border-primary/10 dark:border-green-800/60 overflow-hidden shadow-sm">
                  <p className="text-xs md:text-sm text-primary/70 dark:text-green-300/80 font-medium">Dinheiro</p>
                  <p className="text-base md:text-lg font-bold text-primary dark:text-green-50 truncate" title={formatCurrency(totals.dinheiro)}>
                    {formatCurrency(totals.dinheiro)}
                  </p>
                </div>
                <div className="p-2 md:p-3 bg-primary dark:bg-emerald-900/80 text-primary-foreground dark:text-emerald-50 rounded-lg border border-primary/90 dark:border-emerald-600/50 col-span-2 md:col-span-1 flex flex-col justify-center overflow-hidden shadow-md">
                  <p className="text-xs md:text-sm font-medium text-primary-foreground/80 dark:text-emerald-200/90">TOTAL GERAL</p>
                  <p
                    className="text-lg md:text-xl font-bold truncate"
                    title={formatCurrency(
                      totals.depósito + totals.pix + totals.cartão + totals.dinheiro
                    )}
                  >
                    {formatCurrency(
                      totals.depósito +
                        totals.pix +
                        totals.cartão +
                        totals.dinheiro,
                    )}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                {canEdit && (
                  <button
                    onClick={launchFinancials}
                    disabled={isLaunching}
                    className="bg-primary/90 hover:bg-primary text-primary-foreground font-bold py-2 px-6 rounded-lg shadow-sm shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLaunching ? "Lançando..." : "Lançar no Histórico"}
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <AddReservationModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddReservation={addReservation}
        availableChales={availableChales}
        defaultCheckInDate={selectedDate}
      />

      {editingReservation && (
        <EditReservationModal
          isOpen={!!editingReservation}
          onClose={() => setEditingReservation(null)}
          onUpdateReservation={updateReservation}
          reservationData={editingReservation}
        />
      )}

      <ChaletList
        isOpen={isGuestListModalOpen}
        onClose={() => setIsGuestListModalOpen(false)}
        chales={chales}
        onUpdateReservation={updateReservation}
        selectedDate={selectedDate}
      />

      <ExportGuestsModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        chales={chales}
        selectedDate={selectedDate}
      />
    </div>
  );
}

export default ChalesTab;
